import React from "react";
import { Box, Button, IconButton, Paper, TextField, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  BlocksEditor,
  ChecksEditor,
  EditorSection,
  HelpChannelsEditor,
  ImageFieldsEditor,
  LabelsEditor,
  LinkListEditor,
  MetricsEditor,
  ParagraphsEditor,
  StringListEditor,
} from "./InstitutionalEditorParts";
import InstitutionalIconPicker from "../shared/InstitutionalIconPicker";

function MilestonesEditor({ milestones, onChange }) {
  const list = Array.isArray(milestones) ? milestones : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Marcos da história</Typography>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onChange([...list, { label: "", title: "", desc: "" }])}>
          Adicionar
        </Button>
      </Box>
      {list.map((m, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton size="small" onClick={() => onChange(list.filter((_, j) => j !== i))}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <TextField fullWidth size="small" label="Etiqueta (ex.: Etapa 1)" value={m.label || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], label: e.target.value }; onChange(next);
          }} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" label="Título" value={m.title || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], title: e.target.value }; onChange(next);
          }} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" multiline minRows={2} label="Descrição" value={m.desc || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], desc: e.target.value }; onChange(next);
          }} />
        </Paper>
      ))}
    </Box>
  );
}

function PillarsEditor({ pillars, onChange }) {
  const list = Array.isArray(pillars) ? pillars : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Pilares / áreas</Typography>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onChange([...list, { icon: "", title: "", description: "" }])}>
          Adicionar
        </Button>
      </Box>
      {list.map((p, i) => (
        <Paper key={i} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <IconButton size="small" onClick={() => onChange(list.filter((_, j) => j !== i))}>
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <InstitutionalIconPicker
            value={p.icon || ""}
            onChange={(icon) => {
              const next = [...list];
              next[i] = { ...next[i], icon };
              onChange(next);
            }}
          />
          <TextField fullWidth size="small" label="Título" value={p.title || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], title: e.target.value }; onChange(next);
          }} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" multiline minRows={2} label="Descrição" value={p.description || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], description: e.target.value }; onChange(next);
          }} />
        </Paper>
      ))}
    </Box>
  );
}

function ImpactNumbersEditor({ items, onChange }) {
  const list = Array.isArray(items) ? items : [];
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Números de impacto</Typography>
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onChange([...list, { value: "", label: "", detail: "" }])}>
          Adicionar
        </Button>
      </Box>
      {list.map((n, i) => (
        <Box key={i} sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
          <TextField size="small" label="Valor" value={n.value || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], value: e.target.value }; onChange(next);
          }} />
          <TextField size="small" label="Rótulo" value={n.label || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], label: e.target.value }; onChange(next);
          }} sx={{ flex: 1, minWidth: 120 }} />
          <TextField size="small" label="Detalhe" value={n.detail || ""} onChange={(e) => {
            const next = [...list]; next[i] = { ...next[i], detail: e.target.value }; onChange(next);
          }} sx={{ flex: 1, minWidth: 140 }} />
          <IconButton size="small" onClick={() => onChange(list.filter((_, j) => j !== i))}>
            <DeleteOutlineRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
}

function ObjBlockEditor({ title, subtitle, value, onChange, fields, uploadSubpath = "contents/institutional", defaultExpanded = false, optional = false }) {
  const v = value && typeof value === "object" ? value : {};
  const set = (patch) => onChange({ ...v, ...patch });
  return (
    <EditorSection title={title} subtitle={subtitle} defaultExpanded={defaultExpanded} optional={optional}>
      {fields.map((f) => {
        if (f.type === "paragraphs") {
          return (
            <ParagraphsEditor
              key={f.key}
              label={f.label}
              paragraphs={v[f.key]}
              onChange={(paragraphs) => set({ [f.key]: paragraphs })}
            />
          );
        }
        if (f.type === "strings") {
          return (
            <StringListEditor
              key={f.key}
              label={f.label}
              items={v[f.key]}
              onChange={(items) => set({ [f.key]: items })}
            />
          );
        }
        if (f.type === "links") {
          return (
            <LinkListEditor
              key={f.key}
              label={f.label}
              items={v[f.key]}
              onChange={(items) => set({ [f.key]: items })}
            />
          );
        }
        if (f.type === "image") {
          return (
            <ImageFieldsEditor
              key={f.key}
              label={f.label}
              image={v[f.key] ?? v.image}
              imageAlt={v.imageAlt}
              variant={f.imageVariant || "content"}
              uploadSubpath={uploadSubpath}
              onChange={({ image, imageAlt }) => set({ [f.key]: image, imageAlt })}
            />
          );
        }
        if (f.type === "posterImage") {
          return (
            <ImageFieldsEditor
              key={f.key}
              label={f.label}
              image={v[f.key] || ""}
              imageAlt=""
              variant="poster"
              uploadSubpath={uploadSubpath}
              showAlt={false}
              onChange={({ image }) => set({ [f.key]: image })}
            />
          );
        }
        return (
          <TextField
            key={f.key}
            fullWidth
            size="small"
            multiline={f.multiline}
            minRows={f.multiline ? 2 : undefined}
            label={f.label}
            value={v[f.key] || ""}
            onChange={(e) => set({ [f.key]: e.target.value })}
            sx={{ mb: 1.5 }}
          />
        );
      })}
    </EditorSection>
  );
}

export function VariantSpecificPanels({ v, set }) {
  const variant = v.variant || "informacoes";
  const setBlock = (key, val) => set({ [key]: val });
  const uploadSubpath = `contents/institutional/${variant}`;

  if (variant === "sobre") {
    return (
      <>
        <ObjBlockEditor
          title="Propósito"
          subtitle="Texto de abertura da página Sobre"
          defaultExpanded
          uploadSubpath={uploadSubpath}
          value={v.purpose}
          onChange={(purpose) => setBlock("purpose", purpose)}
          fields={[
            { key: "eyebrow", label: "Etiqueta" },
            { key: "title", label: "Título" },
            { key: "paragraphs", label: "Parágrafos", type: "paragraphs" },
            { key: "sidebarEyebrow", label: "Etiqueta lateral" },
            { key: "sidebarTitle", label: "Título lateral" },
          ]}
        />
        <EditorSection title="Pontos fortes" subtitle="Lista exibida na lateral" defaultExpanded={false}>
          <StringListEditor items={v.strengths} onChange={(strengths) => setBlock("strengths", strengths)} label="Itens" />
        </EditorSection>
        <EditorSection title="História da empresa" subtitle="Linha do tempo e marcos" defaultExpanded={false}>
          <TextField fullWidth size="small" label="Etiqueta" value={v.history?.eyebrow || ""} onChange={(e) => setBlock("history", { ...v.history, eyebrow: e.target.value })} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" label="Título" value={v.history?.title || ""} onChange={(e) => setBlock("history", { ...v.history, title: e.target.value })} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" multiline minRows={2} label="Introdução" value={v.history?.intro || ""} onChange={(e) => setBlock("history", { ...v.history, intro: e.target.value })} sx={{ mb: 1 }} />
          <MilestonesEditor milestones={v.history?.milestones} onChange={(milestones) => setBlock("history", { ...v.history, milestones })} />
        </EditorSection>
        <ObjBlockEditor title="Vídeo institucional" subtitle="Link do YouTube e capa" optional uploadSubpath={uploadSubpath} value={v.video} onChange={(video) => setBlock("video", video)} fields={[
          { key: "badge", label: "Etiqueta" }, { key: "title", label: "Título" },
          { key: "description", label: "Descrição", multiline: true }, { key: "url", label: "Link do vídeo (YouTube ou similar)" },
          { key: "poster", label: "Imagem de capa do vídeo", type: "posterImage" }, { key: "note", label: "Nota", multiline: true },
        ]} />
        <ObjBlockEditor title="Família e origem" subtitle="Foto e texto sobre a família Guerino" uploadSubpath={uploadSubpath} value={v.family} onChange={(family) => setBlock("family", family)} fields={[
          { key: "eyebrow", label: "Etiqueta" }, { key: "title", label: "Título" },
          { key: "image", label: "Foto", type: "image", imageVariant: "content" }, { key: "paragraphs", label: "Parágrafos", type: "paragraphs" },
        ]} />
        <ObjBlockEditor title="Acervo" uploadSubpath={uploadSubpath} value={v.archive} onChange={(archive) => setBlock("archive", archive)} fields={[
          { key: "eyebrow", label: "Etiqueta" }, { key: "title", label: "Título" },
          { key: "image", label: "Foto do acervo", type: "image", imageVariant: "content" }, { key: "paragraphs", label: "Parágrafos", type: "paragraphs" },
          { key: "ctas", label: "Botões", type: "links" },
        ]} />
        <ObjBlockEditor title="Matriz / sede" uploadSubpath={uploadSubpath} value={v.headquarters} onChange={(headquarters) => setBlock("headquarters", headquarters)} fields={[
          { key: "eyebrow", label: "Etiqueta" }, { key: "title", label: "Título" }, { key: "companyName", label: "Razão social" },
          { key: "addressLines", label: "Endereço (linhas)", type: "strings" },
          { key: "phone", label: "Telefone" }, { key: "phoneLabel", label: "Telefone exibido" },
          { key: "ctaLabel", label: "Texto do botão" }, { key: "ctaHref", label: "Link do botão" },
          { key: "citiesEyebrow", label: "Etiqueta cidades" }, { key: "cities", label: "Cidades", type: "strings" },
          { key: "cnpj", label: "CNPJ (SEO)" }, { key: "schemaPhone", label: "Telefone schema.org" },
        ]} />
        <ObjBlockEditor title="Mapa" uploadSubpath={uploadSubpath} value={v.map} onChange={(map) => setBlock("map", map)} fields={[
          { key: "title", label: "Título" }, { key: "subtitle", label: "Subtítulo" },
          { key: "badge", label: "Etiqueta" }, { key: "query", label: "Endereço para o mapa" },
        ]} />
      </>
    );
  }

  if (variant === "informacoes" || variant === "acessibilidade") {
    return (
      <>
        <EditorSection title="Medidas e regras" subtitle="Limites de bagagem, itens proibidos e conferência" defaultExpanded>
          <MetricsEditor metrics={v.metrics} onChange={(metrics) => setBlock("metrics", metrics)} />
          <StringListEditor label="Itens proibidos" items={v.prohibitedItems} onChange={(prohibitedItems) => setBlock("prohibitedItems", prohibitedItems)} />
          <ChecksEditor checks={v.checks} onChange={(checks) => setBlock("checks", checks)} />
        </EditorSection>
        <EditorSection title="Canais no rodapé da página" subtitle="Telefones e contatos exibidos ao final" defaultExpanded={false}>
          <HelpChannelsEditor channels={v.closingCta?.channels} onChange={(channels) => set({ closingCta: { ...v.closingCta, channels } })} />
          <TextField fullWidth multiline minRows={2} label="Nota de rodapé" value={v.footerNote || ""} onChange={(e) => setBlock("footerNote", e.target.value)} />
        </EditorSection>
      </>
    );
  }

  if (variant === "beneficios") {
    return (
      <>
        <EditorSection title="Rótulos da interface">
          <LabelsEditor labels={v.labels} onChange={(labels) => setBlock("labels", labels)} />
        </EditorSection>
        <EditorSection title="Passos e canais">
          <ChecksEditor checks={v.steps} onChange={(steps) => setBlock("steps", steps)} />
          <HelpChannelsEditor channels={v.helpChannels} onChange={(helpChannels) => setBlock("helpChannels", helpChannels)} />
        </EditorSection>
        <EditorSection title="Encerramento (links)">
          <TextField fullWidth size="small" label="Etiqueta" value={v.closingCta?.eyebrow || ""} onChange={(e) => set({ closingCta: { ...v.closingCta, eyebrow: e.target.value } })} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" label="Nota" value={v.closingCta?.note || ""} onChange={(e) => set({ closingCta: { ...v.closingCta, note: e.target.value } })} sx={{ mb: 1 }} />
          <LinkListEditor label="Links úteis" items={v.closingCta?.links} onChange={(links) => set({ closingCta: { ...v.closingCta, links } })} showExternal />
        </EditorSection>
      </>
    );
  }

  if (variant === "sustentabilidade") {
    return (
      <>
        <ImageFieldsEditor
          label="Banner da página"
          image={v.banner?.image}
          imageAlt={v.banner?.imageAlt}
          variant="banner"
          uploadSubpath={uploadSubpath}
          onChange={(banner) => setBlock("banner", { ...v.banner, ...banner })}
        />
        <PillarsEditor pillars={v.pillars} onChange={(pillars) => setBlock("pillars", pillars)} />
        <ImpactNumbersEditor items={v.impactNumbers} onChange={(impactNumbers) => setBlock("impactNumbers", impactNumbers)} />
        <HelpChannelsEditor channels={v.helpChannels} onChange={(helpChannels) => setBlock("helpChannels", helpChannels)} />
      </>
    );
  }

  if (variant === "vale_transporte") {
    return (
      <>
        <EditorSection title="Destaques da página">
          <StringListEditor label="Lista de destaques" items={v.highlights} onChange={(highlights) => setBlock("highlights", highlights)} />
        </EditorSection>
        <ChecksEditor checks={v.employeeSteps} onChange={(employeeSteps) => setBlock("employeeSteps", employeeSteps)} />
        <HelpChannelsEditor channels={v.helpChannels} onChange={(helpChannels) => setBlock("helpChannels", helpChannels)} />
      </>
    );
  }

  if (variant === "trabalhe_conosco") {
    return (
      <>
        <EditorSection title="Passos e áreas">
          <StringListEditor label="Passos (texto simples)" items={(v.steps || []).map((s) => (typeof s === "string" ? s : s.title))} onChange={(steps) => setBlock("steps", steps)} />
          <TextField fullWidth size="small" label="Etiqueta seção áreas" value={v.areasSection?.eyebrow || ""} onChange={(e) => setBlock("areasSection", { ...v.areasSection, eyebrow: e.target.value })} sx={{ mb: 1, mt: 2 }} />
          <TextField fullWidth size="small" label="Título seção áreas" value={v.areasSection?.title || ""} onChange={(e) => setBlock("areasSection", { ...v.areasSection, title: e.target.value })} sx={{ mb: 1 }} />
          <PillarsEditor pillars={v.areas} onChange={(areas) => setBlock("areas", areas)} />
        </EditorSection>
        <EditorSection title="Formulário de candidatura (textos)">
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Edite títulos e mensagens exibidos no formulário. Campos técnicos do formulário são preservados ao salvar.
          </Typography>
          <TextField fullWidth size="small" label="Título do formulário" value={v.formIntro?.title || ""} onChange={(e) => setBlock("formIntro", { ...v.formIntro, title: e.target.value })} sx={{ mb: 1 }} />
          <TextField fullWidth multiline minRows={2} label="Descrição" value={v.formIntro?.description || ""} onChange={(e) => setBlock("formIntro", { ...v.formIntro, description: e.target.value })} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" label="Botão WhatsApp" value={v.formIntro?.submitButtonWhatsApp || ""} onChange={(e) => setBlock("formIntro", { ...v.formIntro, submitButtonWhatsApp: e.target.value })} sx={{ mb: 1 }} />
          <TextField fullWidth size="small" label="Botão contato" value={v.formIntro?.submitButtonContact || ""} onChange={(e) => setBlock("formIntro", { ...v.formIntro, submitButtonContact: e.target.value })} />
        </EditorSection>
        <HelpChannelsEditor channels={v.helpChannels} onChange={(helpChannels) => setBlock("helpChannels", helpChannels)} />
      </>
    );
  }

  return null;
}

export function SectionsWithBlocksEditor({ sections, onChange }) {
  const list = Array.isArray(sections) ? sections : [];
  return (
    <>
      {list.map((sec, si) => (
        <Paper key={si} variant="outlined" sx={{ p: 2, mb: 2, borderColor: "primary.light" }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Conteúdo interno: {sec.title || sec.id || `Seção ${si + 1}`}
          </Typography>
          <BlocksEditor
            blocks={sec.blocks}
            onChange={(blocks) => {
              const next = [...list];
              next[si] = { ...next[si], blocks };
              onChange(next);
            }}
          />
          <LinkListEditor
            label="Links oficiais desta seção"
            items={sec.links}
            onChange={(links) => {
              const next = [...list];
              next[si] = { ...next[si], links };
              onChange(next);
            }}
            showExternal
          />
        </Paper>
      ))}
    </>
  );
}
